import React, { useMemo } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import FieldView, { FieldViewProps } from "../../../../../../../components/Form/FieldView";
import { InvoicesCharges } from "../../../../../../../models/invoice.charges";
import { formatDateWithoutTimeZone } from "../../../../../../../util/Format";
import useStyles from "./styles";

interface Props {
  invoice: InvoicesCharges;
}

const InvoiceDetails = (props: Props) => {
  const { invoice } = props;
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const formatMonetaryField = (value?: string | number) => {
    // if (value) return `$ ${value}`;
    return value;
  };

  const fields: FieldViewProps[] = useMemo(
    () => [
      { label: translate("shipment"), value: invoice?.invoiceShipment?.shipment },
      { label: translate("po"), value: invoice?.invoiceShipment?.poNumber },
      { label: `${translate("part")} #`, value: invoice?.invoiceShipment?.partNumber },
      { label: translate("origin"), value: invoice?.invoiceShipment?.origin },

      { label: translate("country"), value: invoice?.invoiceShipment?.originCountry },
      { label: translate("destination"), value: invoice?.invoiceShipment?.destination },
      {
        label: translate("etd"),
        value: formatDateWithoutTimeZone(invoice?.invoiceShipment?.etd?.toString()),
      },
      {
        label: translate("eta"),
        value: formatDateWithoutTimeZone(invoice?.invoiceShipment?.eta?.toString()),
      },

      { label: translate("mode"), value: invoice?.invoiceShipment?.mode ? translate(invoice.invoiceShipment.mode.toLowerCase()) : "" },
      { label: translate("containerType"), value: invoice?.invoiceShipment?.container },
      { label: translate("supplierName"), value: invoice?.invoiceShipment?.supplierName },
      { label: translate("houseBill"), value: invoice?.invoiceShipment?.houseBill },

      { label: translate("masterBill"), value: invoice?.invoiceShipment?.masterBill },
      { label: translate("containers"), value: invoice?.invoiceShipment?.containers },
      { label: `${translate("weight")} (KG)`, value: invoice?.invoiceShipment?.weight },
      { label: translate("teu"), value: invoice?.invoiceShipment?.teu },

      { label: translate("cbm"), value: invoice?.invoiceShipment?.cbm },
      { label: translate("cartons"), value: invoice?.invoiceShipment?.cartons },
      { label: translate("hblCountMode"), value: invoice?.invoiceShipment?.hblMode },
      { label: translate("chargeCode"), value: invoice?.chargeCode },

      { label: translate("description"), value: invoice?.description },
      { label: translate("overseasAmount"), value: formatMonetaryField(invoice?.overseasAmount) },
      { label: translate("exchangeRateCurrency"), value: invoice?.exchangeRateCurrency },
      { label: translate("exchangeRate"), value: invoice?.exchangeRate },

      {
        label: translate("invoicedLocalAmount"),
        value: formatMonetaryField(invoice?.invoicedLocalAmount),
      },
      { label: translate("gstAmount"), value: formatMonetaryField(invoice?.gstAmount) },
      { label: translate("invoiceNumber"), value: invoice?.invoiceNumber },
      {
        label: translate("invoiceDate"),
        value: formatDateWithoutTimeZone(invoice?.invoiceDate?.toString()),
      },

      {
        label: translate("invoiceDueDate"),
        value: formatDateWithoutTimeZone(invoice?.invoiceDueDate?.toString()),
      },
      {
        label: translate("delivered"),
        value: formatDateWithoutTimeZone(invoice?.invoiceShipment?.delivered?.toString()),
      },
      { label: translate("autoRate"), value: invoice?.autoRate },
      ...(invoice?.cottonOnAdultsApportionment
        ? [
            {
              label: `${translate("cottonOnAdultsApportionment")} %`,
              value: invoice?.cottonOnAdultsApportionment,
            },
          ]
        : []),
      ...(invoice?.cottonOnAdultsLocalAmount
        ? [
            {
              label: translate("cottonOnAdultsLocalAmount"),
              value: formatMonetaryField(invoice?.cottonOnAdultsLocalAmount),
            },
          ]
        : []),
      ...(invoice?.ceresLifeApportionment
        ? [{ label: translate("ceresLifeApportionment"), value: invoice?.ceresLifeApportionment }]
        : []),
      ...(invoice?.ceresLifeLocalAmount
        ? [
            {
              label: translate("ceresLifeLocalAmount"),
              value: formatMonetaryField(invoice?.ceresLifeLocalAmount),
            },
          ]
        : []),

      ...(invoice?.bodyApportionment
        ? [{ label: translate("bodyApportionment"), value: invoice?.bodyApportionment }]
        : []),

      ...(invoice?.bodyLocalAmount
        ? [
            {
              label: translate("bodyLocalAmount"),
              value: formatMonetaryField(invoice?.bodyLocalAmount),
            },
          ]
        : []),

      ...(invoice?.factorieApportionment
        ? [
            {
              label: `${translate("factorieApportionment")} %`,
              value: invoice?.factorieApportionment,
            },
          ]
        : []),

      ...(invoice?.factorieLocalAmount
        ? [
            {
              label: translate("factorieLocalAmount"),
              value: formatMonetaryField(invoice?.factorieLocalAmount),
            },
          ]
        : []),
      ...(invoice?.kidsApportionment
        ? [{ label: `${translate("kidsApportionment")} %`, value: invoice?.kidsApportionment }]
        : []),
      ...(invoice?.kidsLocalAmount
        ? [
            {
              label: translate("kidsLocalAmount"),
              value: formatMonetaryField(invoice?.kidsLocalAmount),
            },
          ]
        : []),

      ...(invoice?.rubiApportionment
        ? [{ label: `${translate("rubiApportionment")} %`, value: invoice?.rubiApportionment }]
        : []),

      ...(invoice?.rubiLocalAmount
        ? [
            {
              label: translate("rubiLocalAmount"),
              value: formatMonetaryField(invoice?.rubiLocalAmount),
            },
          ]
        : []),

      ...(invoice?.foundationApportionment
        ? [
            {
              label: `${translate("foundationApportionment")} %`,
              value: invoice?.foundationApportionment,
            },
          ]
        : []),

      ...(invoice?.foundationLocalAmount
        ? [
            {
              label: translate("foundationLocalAmount"),
              value: formatMonetaryField(invoice?.foundationLocalAmount),
            },
          ]
        : []),

      ...(invoice?.typoApportionment
        ? [{ label: `${translate("typoApportionment")} %`, value: invoice?.typoApportionment }]
        : []),

      ...(invoice?.typoLocalAmount
        ? [
            {
              label: translate("typoLocalAmount"),
              value: formatMonetaryField(invoice?.typoLocalAmount),
            },
          ]
        : []),

      ...(invoice?.gamedayApportionment
        ? [
            {
              label: `${translate("gamedayApportionment")} %`,
              value: invoice?.gamedayApportionment,
            },
          ]
        : []),

      ...(invoice?.gamedayLocalAmount
        ? [
            {
              label: translate("gamedayLocalAmount"),
              value: formatMonetaryField(invoice?.gamedayLocalAmount),
            },
          ]
        : []),

      ...(invoice?.supreApportionment
        ? [{ label: `${translate("supreApportionment")} %`, value: invoice?.supreApportionment }]
        : []),

      ...(invoice?.supreLocalAmount
        ? [
            {
              label: translate("supreLocalAmount"),
              value: formatMonetaryField(invoice?.supreLocalAmount),
            },
          ]
        : []),

      ...(invoice?.fixturesApportionment
        ? [
            {
              label: `${translate("fixturesApportionment")} %`,
              value: invoice?.fixturesApportionment,
            },
          ]
        : []),

      ...(invoice?.fixturesLocalAmount
        ? [
            {
              label: translate("fixturesLocalAmount"),
              value: formatMonetaryField(invoice?.fixturesLocalAmount),
            },
          ]
        : []),

      ...(invoice?.consumablesApportionment
        ? [
            {
              label: `${translate("consumablesApportionment")} %`,
              value: invoice?.consumablesApportionment,
            },
          ]
        : []),

      ...(invoice?.consumablesLocalAmount
        ? [
            {
              label: translate("consumablesLocalAmount"),
              value: formatMonetaryField(invoice?.consumablesLocalAmount),
            },
          ]
        : []),

      ...(invoice?.otherApportionment
        ? [{ label: `${translate("otherApportionment")} %`, value: invoice?.otherApportionment }]
        : []),

      ...(invoice?.otherLocalAmount
        ? [
            {
              label: translate("otherLocalAmount"),
              value: formatMonetaryField(invoice?.otherLocalAmount),
            },
          ]
        : []),
    ],
    [invoice]
  );

  return (
    <div>
      <Grid
        container
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        {fields.map((field) => {
          return (
            <Grid key={field?.label} item xs={12} sm={6} md={6} lg={3}>
              <FieldView
                label={field.label}
                value={field.value}
                labelClass={classes.ellipsis}
                emptyValueInfo="N/A"
              />
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
};

export default InvoiceDetails;
